"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import React, { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { CaretLeft, CaretRight, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "@/navigation";
import "swiper/css";
import "swiper/css/navigation";

export interface StatCard {
	valueKey: string;
	labelKey: string;
	variant: "light" | "primary";
}

export interface StoryData {
	id: number;
	categoryKey: string;
	titleKey: string;
	descKey: string;
	stats: StatCard[];
	image: string;
	link: string;
}

const DEFAULT_STORIES_DATA: StoryData[] = [
	{
		id: 1,
		categoryKey: "story1Category",
		titleKey: "story1Title",
		descKey: "story1Desc",
		stats: [
			{ valueKey: "story1Stat1Value", labelKey: "story1Stat1Label", variant: "light" },
			{ valueKey: "story1Stat2Value", labelKey: "story1Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/consumer-electronics.webp",
		link: "/cases",
	},
	{
		id: 2,
		categoryKey: "story2Category",
		titleKey: "story2Title",
		descKey: "story2Desc",
		stats: [
			{ valueKey: "story2Stat1Value", labelKey: "story2Stat1Label", variant: "light" },
			{ valueKey: "story2Stat2Value", labelKey: "story2Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/aerospace.webp",
		link: "/cases",
	},
	{
		id: 3,
		categoryKey: "story3Category",
		titleKey: "story3Title",
		descKey: "story3Desc",
		stats: [
			{ valueKey: "story3Stat1Value", labelKey: "story3Stat1Label", variant: "light" },
			{ valueKey: "story3Stat2Value", labelKey: "story3Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/robotics.webp",
		link: "/cases",
	},
	{
		id: 4,
		categoryKey: "story4Category",
		titleKey: "story4Title",
		descKey: "story4Desc",
		stats: [
			{ valueKey: "story4Stat1Value", labelKey: "story4Stat1Label", variant: "light" },
			{ valueKey: "story4Stat2Value", labelKey: "story4Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/automotive.webp",
		link: "/cases",
	},
	{
		id: 5,
		categoryKey: "story5Category",
		titleKey: "story5Title",
		descKey: "story5Desc",
		stats: [
			{ valueKey: "story5Stat1Value", labelKey: "story5Stat1Label", variant: "light" },
			{ valueKey: "story5Stat2Value", labelKey: "story5Stat2Label", variant: "primary" },
		],
		image: "/image/new-version/home/case/industrial-equipment.webp",
		link: "/cases",
	},
];

function StatCardItem({ stat, t }: { stat: StatCard; t: (key: string) => string }) {
	const isLight = stat.variant === "light";

	return (
		<div
			className={`flex max-w-[205px] flex-col justify-center rounded-lg px-4 py-3 sm:px-5 sm:py-4 ${
				isLight ? "border-2 border-themePrimary400 bg-white" : "bg-themePrimary400"
			}`}
		>
			<div
				className={`mb-2 text-center text-xl font-bold sm:text-2xl md:text-[28px] ${
					isLight ? "text-black" : "text-white"
				}`}
			>
				{t(stat.valueKey)}
			</div>
			<div className={`text-center text-sm ${isLight ? "text-black" : "text-white"}`}>{t(stat.labelKey)}</div>
		</div>
	);
}

export interface SuccessStoryProps {
	translationNamespace?: string;
	stories?: StoryData[];
	equalSplit?: boolean;
}

export default function SuccessStory({
	translationNamespace = "successStory",
	stories = DEFAULT_STORIES_DATA,
	equalSplit = false,
}: SuccessStoryProps) {
	const t = useTranslations(translationNamespace);
	const swiperRef = useRef<SwiperType | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	const handleSlideChange = (swiper: SwiperType) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
				</div>

				<div className="relative w-full">
					<button
						onClick={() => swiperRef.current?.slidePrev()}
						disabled={isBeginning}
						className={`group absolute left-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center transition-colors md:h-12 md:w-12 ${
							isBeginning ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100"
						}`}
						aria-label="Previous slide"
					>
						<CaretLeft
							size={28}
							weight="bold"
							className="text-themePrimary400 transition-colors group-hover:text-black md:size-8"
						/>
					</button>

					<button
						onClick={() => swiperRef.current?.slideNext()}
						disabled={isEnd}
						className={`group absolute right-0 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center transition-colors md:h-12 md:w-12 ${
							isEnd ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100"
						}`}
						aria-label="Next slide"
					>
						<CaretRight
							size={28}
							weight="bold"
							className="text-themePrimary400 transition-colors group-hover:text-black md:size-8"
						/>
					</button>

					<div className="container">
						<Swiper
							modules={[Navigation, Autoplay]}
							spaceBetween={24}
							slidesPerView={1}
							onSwiper={(swiper) => {
								swiperRef.current = swiper;
							}}
							onSlideChange={handleSlideChange}
							className="overflow-hidden"
							autoHeight
						>
							{stories.map((story) => (
								<SwiperSlide key={story.id}>
									<article className="overflow-hidden rounded-xl border-4 border-[#e5e5e5] bg-white px-4 py-8 shadow-sm transition-shadow duration-300 sm:border-[6px] sm:px-6 sm:py-10 md:border-[8px] md:px-8 md:py-12 lg:px-10 lg:py-[50px]">
										<div className="flex flex-col gap-6 sm:gap-8 md:gap-10 lg:flex-row lg:items-stretch">
											<div
												className={`relative aspect-[4/3] w-full min-w-0 overflow-hidden bg-white py-2 ${
													equalSplit ? "lg:flex-1" : "lg:w-[45%]"
												}`}
											>
												<Image
													src={story.image}
													alt={t(story.titleKey)}
													width={1000}
													height={1000}
													className="h-full object-cover"
													sizes={
														equalSplit ? "(max-width: 1024px) 100vw, 50vw" : "(max-width: 1024px) 100vw, 40vw"
													}
												/>
											</div>

											<div className={`flex min-w-0 flex-col ${equalSplit ? "lg:flex-1" : "flex-1"}`}>
												<span className="mb-3 w-fit border-b-[2.5px] border-themePrimary400 text-sm font-semibold text-black sm:mb-4 sm:text-base">
													{t(story.categoryKey)}
												</span>

												<h3 className="mb-8 mt-4 text-lg font-bold text-black sm:text-xl md:text-2xl">
													{t(story.titleKey)}
												</h3>

												<p className="mb-8 text-sm leading-relaxed text-black md:text-base">
													{t(story.descKey)}
												</p>

												<div className="flex flex-wrap gap-3 sm:gap-4">
													{story.stats.map((stat, idx) => (
														<StatCardItem key={idx} stat={stat} t={t} />
													))}
												</div>

												{story.link.startsWith("http") ? (
													<a
														href={story.link}
														target="_blank"
														rel="noopener noreferrer"
														className="mt-8 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
													>
														{t("readFullCaseStudy")}
														<ArrowUpRight size={16} weight="bold" />
													</a>
												) : (
													<Link
														href={story.link}
														className="mt-8 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
													>
														{t("readFullCaseStudy")}
														<ArrowUpRight size={16} weight="bold" />
													</Link>
												)}
											</div>
										</div>
									</article>
								</SwiperSlide>
							))}
						</Swiper>
					</div>

					<div className="mt-4 flex items-center justify-center gap-4 md:hidden">
						<button
							onClick={() => swiperRef.current?.slidePrev()}
							disabled={isBeginning}
							className={`group flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#2E7D32] transition-all duration-300 ${
								isBeginning
									? "cursor-not-allowed opacity-30"
									: "cursor-pointer hover:bg-[#2E7D32] hover:text-white"
							}`}
							aria-label="Previous slide"
						>
							<CaretLeft size={20} weight="bold" className="text-[#2E7D32] group-hover:text-white" />
						</button>

						<div className="flex items-center gap-2">
							{stories.map((_, idx) => (
								<button
									key={idx}
									onClick={() => swiperRef.current?.slideTo(idx)}
									className={`h-2 rounded-full transition-all duration-300 ${
										swiperRef.current?.activeIndex === idx || (!swiperRef.current && idx === 0)
											? "w-6 bg-[#2E7D32]"
											: "w-2 bg-gray-300 hover:bg-gray-400"
									}`}
									aria-label={`Go to slide ${idx + 1}`}
								/>
							))}
						</div>

						<button
							onClick={() => swiperRef.current?.slideNext()}
							disabled={isEnd}
							className={`group flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#2E7D32] transition-all duration-300 ${
								isEnd ? "cursor-not-allowed opacity-30" : "cursor-pointer hover:bg-[#2E7D32] hover:text-white"
							}`}
							aria-label="Next slide"
						>
							<CaretRight size={20} weight="bold" className="text-[#2E7D32] group-hover:text-white" />
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}
